import React, { createContext, useContext, useEffect, useState, useCallback } from "react"
import {
 clearTenantThemeInlineStyle,
 applyDefaultTenantTheme,
} from "../theme/applyTenantTheme"
type Theme ="light"|"dark"|"system"
interface ThemeContextValue {
 theme: Theme
 resolvedTheme:"light"|"dark"
setTheme: (theme: Theme) => void
}

const ThemeContext = createContext<ThemeContextValue>({
 theme:"light",
 resolvedTheme:"light",
 setTheme: () => {},
})

const STORAGE_KEY ="erb-theme"
const DEFAULT_THEME: Theme ="light"
function getSystemPreference():"light"|"dark"{
 if (typeof window === "undefined") return"light"
return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark":"light"}

function resolveTheme(theme: Theme):"light"|"dark"{
 if (theme === "system") return getSystemPreference()
 return theme
}

function applyTheme(resolved:"light"|"dark") {
 const root = document.documentElement
 if (resolved === "dark") {
 clearTenantThemeInlineStyle(root)
 root.classList.add("dark")
 } else {
 root.classList.remove("dark")
 applyDefaultTenantTheme(root)
 }
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
 const [theme, setThemeState] = useState<Theme>(() => {
 if (typeof window === "undefined") return DEFAULT_THEME
 try {
 const stored = localStorage.getItem(STORAGE_KEY)
 if (stored === "light" || stored === "dark") return stored
 } catch {}
 return DEFAULT_THEME
 })

 const [resolvedTheme, setResolvedTheme] = useState<"light"|"dark">(() =>
 resolveTheme(theme)
 )

 const setTheme = useCallback((newTheme: Theme) => {
 setThemeState(newTheme)
 try {
 localStorage.setItem(STORAGE_KEY, newTheme)
 } catch {}
 }, [])

 useEffect(() => {
 const resolved = resolveTheme(theme)
 setResolvedTheme(resolved)
 applyTheme(resolved)
 }, [theme])

 useEffect(() => {
 if (theme !== "system") return

 const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)")

 const handleChange = () => {
 const resolved = getSystemPreference()
 setResolvedTheme(resolved)
 applyTheme(resolved)
 }

 mediaQuery.addEventListener("change", handleChange)
 return () => mediaQuery.removeEventListener("change", handleChange)
 }, [theme])

 return (
 <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme }}>
 {children}
 </ThemeContext.Provider>
 )
}

export function useTheme() {
 const context = useContext(ThemeContext)
 if (!context) {
 throw new Error("useTheme must be used within a ThemeProvider")
 }
 return context
}
